import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Activity,
  Loader2,
  Lock,
  AlertTriangle,
  HelpCircle,
  Info,
  Plus,
  X,
  Zap,
  ThumbsUp,
  ThumbsDown,
  History as HistoryIcon,
  Archive,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { useFastCheck } from "@/contexts/FastCheckContext";
import useGuestSession from "@/hooks/useGuestSession";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { ScanAPI } from "@/services/api";
import OperatorGuide from "@/components/OperatorGuide";
import OperatorHistoryPanel from "@/components/OperatorHistoryPanel";
import FastCheckPanel from "@/components/FastCheckPanel";
import GuestExhaustedModal from "@/components/GuestExhaustedModal";

const VERDICT_META = {
  true: { label: "VERIFIED TRUE", accent: "#22c55e", Icon: ShieldCheck },
  mostly_true: { label: "MOSTLY TRUE", accent: "#84cc16", Icon: ShieldCheck },
  mixed: { label: "MIXED SIGNAL", accent: "#f59e0b", Icon: HelpCircle },
  mostly_false: { label: "MOSTLY FALSE", accent: "#fb7185", Icon: ShieldX },
  false: { label: "VERIFIED FALSE", accent: "#FF0033", Icon: ShieldX },
  unverifiable: { label: "UNVERIFIABLE", accent: "#94a3b8", Icon: HelpCircle },
};

// Verdicts that lock the historical record into read-only on re-open.
// Any returned verdict — yes (green), no (red), mixed (amber), or
// unverifiable (slate) — locks the scan input. Per spec, the operator
// must not be able to chat with the engine after a verdict ships; the
// "next action" surface will live on the verdict card itself.
const FROZEN_VERDICTS = new Set([
  "true",
  "mostly_true",
  "false",
  "mostly_false",
  "mixed",
  "unverifiable",
]);

const confidenceCopy = (n) => {
  if (n >= 80)
    return `${n}% — Represents a high degree of certainty derived from robust cross-referencing of multiple diverse and corroborating sources.`;
  if (n >= 60)
    return `${n}% — Solid signal with multiple supporting sources, but at least one snippet introduced friction or partial conflict.`;
  if (n >= 40)
    return `${n}% — Mixed evidence. Sources disagree or only loosely address the claim — treat the verdict as provisional.`;
  if (n >= 20)
    return `${n}% — Weak evidence base. Sources are tangential, opinion-driven, or insufficient to support a firm verdict.`;
  return `${n}% — Very low confidence. The retrieved snippets cannot meaningfully validate or refute the claim.`;
};

const VERDICT_BADGES = {
  // Maps each AI-verdict bucket to a three-tier human-readable trust badge.
  // Rendered as a prominent sticker inside the verdict card so the operator
  // gets an instant visual confirmation regardless of their familiarity
  // with our internal verdict taxonomy.
  true:         { tier: "green",  label: "Verified Source",            desc: "Something you can trust completely — backed by solid empirical data and corroborated sources." },
  mostly_true:  { tier: "green",  label: "Verified Source",            desc: "Something you can trust completely — backed by solid empirical data and corroborated sources." },
  false:        { tier: "red",    label: "Unverified / Untrusted Source", desc: "Fabricated, heavily manipulated, or actively debunked information. Do not propagate." },
  mostly_false: { tier: "red",    label: "Unverified / Untrusted Source", desc: "Fabricated, heavily manipulated, or actively debunked information. Do not propagate." },
  mixed:        { tier: "yellow", label: "Inconclusive / Mixed Data",  desc: "Context is suspect; data is unverified or conflicting across cited sources." },
  unverifiable: { tier: "yellow", label: "Inconclusive / Mixed Data",  desc: "Context is suspect; data is unverified or conflicting across cited sources." },
};

const TIER_STYLES = {
  green:  { bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.55)",  accent: "#22c55e", IconCmp: ThumbsUp,    glyph: "👍" },
  red:    { bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.55)",  accent: "#ef4444", IconCmp: ThumbsDown,  glyph: "👎" },
  yellow: { bg: "rgba(234,179,8,0.10)",  border: "rgba(234,179,8,0.55)",  accent: "#eab308", IconCmp: AlertTriangle, glyph: "⚠️" },
};

const TrustBadge = ({ verdict }) => {
  const meta = VERDICT_BADGES[verdict] || VERDICT_BADGES.unverifiable;
  const style = TIER_STYLES[meta.tier];
  const Icon = style.IconCmp;
  return (
    <div
      data-testid="trust-badge"
      data-trust-tier={meta.tier}
      className="flex items-start gap-3 px-4 lg:px-5 py-3 border-b"
      style={{
        backgroundColor: style.bg,
        borderBottomColor: style.border,
      }}
    >
      <div
        className="shrink-0 w-9 h-9 flex items-center justify-center border-2"
        style={{ borderColor: style.border, backgroundColor: "#020617" }}
        aria-hidden
      >
        <Icon size={18} style={{ color: style.accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <p
          data-testid="trust-badge-label"
          className="font-mono text-[12px] lg:text-[12.5px] uppercase tracking-[0.2em] font-bold leading-tight"
          style={{ color: style.accent }}
        >
          {meta.label}
        </p>
        <p className="mt-1 text-[12px] lg:text-[12.5px] text-[#cbd5e1] leading-relaxed">
          {meta.desc}
        </p>
      </div>
    </div>
  );
};

const VerdictPanel = ({ verdict }) => {
  const meta = VERDICT_META[verdict.verdict] || VERDICT_META.unverifiable;
  const Icon = meta.Icon;
  return (
    <div
      data-testid="verdict-panel"
      data-verdict={verdict.verdict}
      className="border bg-[#0b0f19]/80 mb-4 lg:mb-5"
      style={{ borderColor: `${meta.accent}55` }}
    >
      <TrustBadge verdict={verdict.verdict} />
      <div
        data-verdict-band={verdict.verdict}
        className="flex items-center justify-between gap-3 px-4 lg:px-5 py-2.5 border-b"
        style={{
          borderColor: `${meta.accent}33`,
          backgroundColor: `${meta.accent}12`,
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon
            size={13}
            data-verdict-icon={verdict.verdict}
            style={{ color: meta.accent }}
            className="shrink-0"
          />
          <span
            data-testid="verdict-label"
            data-verdict-label={verdict.verdict}
            className="font-mono text-[10px] lg:text-[11px] uppercase tracking-[0.24em] font-bold truncate"
            style={{ color: meta.accent }}
          >
            {meta.label}
          </span>
        </div>
        <div
          data-testid="verdict-confidence"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#cbd5e1] flex items-center gap-1.5 shrink-0"
        >
          Confidence{" "}
          <span className="text-[#f1f5f9] font-bold tabular-nums">
            {verdict.confidence}%
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="confidence-info-trigger"
                aria-label="What does this confidence score mean?"
                className="w-4 h-4 flex items-center justify-center text-[#94a3b8] hover:text-[#FF0033] transition-colors"
              >
                <Info size={11} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid="confidence-tooltip"
              side="bottom"
              align="end"
              sideOffset={6}
              className="w-72 max-w-[calc(100vw-2rem)] text-[11px] font-mono leading-relaxed bg-[#020617] text-[#f1f5f9] border border-[#FF0033]/40 p-3 rounded-none shadow-2xl"
            >
              {confidenceCopy(verdict.confidence)}
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="px-4 lg:px-5 py-3.5">
        <p
          data-testid="verdict-summary"
          className="text-[#f1f5f9] text-[13px] lg:text-sm leading-relaxed mb-2.5 break-words"
        >
          {verdict.summary}
        </p>
        <p
          data-testid="verdict-analysis"
          className="font-mono text-[11px] text-[#94a3b8] leading-relaxed break-words"
        >
          {verdict.analysis}
        </p>
      </div>
    </div>
  );
};

const EvidenceCard = ({ item, idx }) => (
  <li
    data-testid={`evidence-item-${item.id || idx}`}
    className="group relative border border-white/[0.07] bg-white/[0.015] hover:bg-white/[0.04] hover:border-[#FF0033]/40 transition-colors min-w-0"
  >
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`evidence-link-${item.id || idx}`}
      className="block p-4 lg:p-5 h-full min-w-0"
    >
      <div className="flex items-center justify-between gap-2 mb-2.5 min-w-0">
        <span className="font-mono text-[10px] text-[#94a3b8] tabular-nums shrink-0">
          {String(item.position || idx + 1).padStart(2, "0")}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-widest text-[#94a3b8] group-hover:text-[#FF0033] transition-colors truncate min-w-0">
          {item.source ? item.source : "OPEN ↗"}
        </span>
      </div>
      <h3 className="text-sm lg:text-[15px] font-bold text-[#f1f5f9] group-hover:text-[#FF0033] transition-colors tracking-tight mb-1.5 leading-snug break-words">
        {item.title}
      </h3>
      <p className="text-[12px] text-[#94a3b8] font-mono leading-relaxed mb-3 break-words whitespace-normal">
        {item.summary}
      </p>
      <span className="flex items-center gap-1.5 font-mono text-[10px] text-[#64748b] group-hover:text-[#FF0033] transition-colors min-w-0">
        <span className="truncate flex-1 min-w-0">{item.link}</span>
        <ArrowRight size={11} className="shrink-0" />
      </span>
    </a>
  </li>
);

const ScanInterface = () => {
  const { user, status, openAuthModal, setCredits } = useAuth();
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scan, setScan] = useState(null);
  const [error, setError] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  // `scanCompleted` flips true the moment the Red Scan ships a verdict.
  // It only hides the Red button — Yellow Fact-Check stays clickable and
  // the textarea stays editable until Yellow finishes. The two-pass UX:
  //   Red Scan → hide Red, allow Yellow → Yellow Fact-Check → full archive.
  const [scanCompleted, setScanCompleted] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [fastChecking, setFastChecking] = useState(false);
  const { setResults: setFastResults } = useFastCheck();
  const { guest, guestExhausted, spendGuestCredit } = useGuestSession();
  const [guestModal, setGuestModal] = useState({ open: false, reason: null });
  const isMobile = useIsMobile();

  const isAuthed = status === "authed";
  const hasResults = Boolean(scan?.evidence?.length);

  // Greeting first-name strictly from the user's profile NAME.
  // If no `name` is available (e.g. magic-link signup with email only),
  // we fall back to a bare "Hi" — we never expose the email prefix.
  const firstName = (() => {
    if (!user?.name) return "";
    const trimmed = user.name.trim();
    if (!trimmed) return "";
    return trimmed.split(/\s+/)[0];
  })();

  // Mobile-only: after a guest signs in, run their pending pre-auth query
  // automatically so they land on the result with 5 credits remaining (7 - 2).
  useEffect(() => {
    if (!isAuthed || !isMobile) return;
    if (typeof window === "undefined") return;
    const pending = window.localStorage.getItem("cmh-pending-scan");
    if (!pending) return;
    window.localStorage.removeItem("cmh-pending-scan");
    setValue(pending);
    setError("");
    setLoading(true);
    (async () => {
      try {
        const data = await ScanAPI.execute(pending);
        setScan(data);
        // Red Scan complete: hide the Red button so credits can't be
        // accidentally double-spent, but leave the textarea editable
        // and the Yellow Fact-Check button visible for the optional
        // second-pass verification before the workspace fully archives.
        if (FROZEN_VERDICTS.has(data?.verdict?.verdict)) {
          setScanCompleted(true);
        }
        if (typeof data.credits_remaining === "number") {
          setCredits(data.credits_remaining);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : "Scan failed. Please retry in a moment."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthed, isMobile, setCredits]);

  // Deep-link: ?scan=<id> hydrates a past scan inline (and applies the lock).
  useEffect(() => {
    if (!isAuthed) return;
    const params = new URLSearchParams(window.location.search);
    const scanId = params.get("scan");
    if (!scanId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await ScanAPI.detail(scanId);
        if (!cancelled) {
          setScan(data);
          if (data?.query) setValue(data.query);
          // Rehydrate any saved Fast Fact-Check sources tied to this scan
          // so the operator gets the full audit trail back, not just the
          // Red verdict.
          if (data?.fast_check?.sources?.length) {
            setFastResults(data.fast_check);
          } else {
            setFastResults(null);
          }
          // Restoring an archived scan via ?scan= deep-link → full lock.
          if (FROZEN_VERDICTS.has(data?.verdict?.verdict)) {
            setReadOnly(true);
            setScanCompleted(true);
          }
        }
      } catch {
        /* ignore */
      } finally {
        const cleanUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  const handleNewScan = () => {
    setScan(null);
    setValue("");
    setError("");
    setReadOnly(false);
    setScanCompleted(false);
    setLoading(false);
    // Wipe the persisted Fast Fact-Check payload too — otherwise the
    // collapsible panel + sources list from the previous claim rehydrate
    // from localStorage and look like they belong to the brand-new scan.
    setFastResults(null);
  };

  const handleHistoryClick = () => {
    if (!isAuthed) {
      setGuestModal({ open: true, reason: "history" });
      return;
    }
    setHistoryOpen(true);
  };

  const handleFastCheck = async () => {
    if (fastChecking || readOnly) return;
    setError("");
    if (!isAuthed) {
      // Guests can also be offered the fast-check, but only if they still
      // have their guest credit. After that, they're behind the auth wall.
      if (guestExhausted) {
        setGuestModal({ open: true, reason: "exhausted" });
        return;
      }
    }
    const query = value.trim();
    if (query.length < 3) {
      setError("Enter at least 3 characters to run a fast-check.");
      return;
    }
    if (!isAuthed) {
      // Fast-check requires auth on the server. Soft-block guests to the
      // auth modal instead of burning their guest credit on it.
      setGuestModal({ open: true, reason: "exhausted" });
      return;
    }
    setFastChecking(true);
    setFastResults(null);
    try {
      // Attach the current scan_id so the backend persists these sources
      // onto the parent Red Scan record — restoring from history then
      // rehydrates both verdict + fast-check in one go.
      const data = await ScanAPI.fastCheck(query, scan?.scan_id || null);
      setFastResults(data);
      // If Yellow runs AFTER a Red verdict has shipped, this is the
      // final-pass verification → trigger the full workspace archive.
      // If Yellow runs standalone (no prior Red scan), it stays a
      // lightweight source-only check and the workspace stays open.
      if (scanCompleted) {
        setReadOnly(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Fast-check failed. Please retry."
      );
    } finally {
      setFastChecking(false);
    }
  };

  const handleExecute = async () => {
    if (loading || readOnly) return;
    setError("");
    const query = value.trim();
    if (query.length < 3) {
      setError("Enter at least 3 characters to execute a scan.");
      return;
    }

    // ---- Guest flow ----
    if (!isAuthed) {
      // Mobile: hard auth gate. Stash the query so it auto-runs the moment
      // the user signs in (Phase 3 spec — see useEffect above).
      if (isMobile) {
        try {
          window.localStorage.setItem("cmh-pending-scan", query);
        } catch {
          /* storage blocked */
        }
        openAuthModal({ reason: "Sign in to see your results." });
        return;
      }
      // Desktop / web: original 1-free-scan guest funnel.
      if (guestExhausted) {
        setGuestModal({ open: true, reason: "exhausted" });
        return;
      }
      const spent = spendGuestCredit();
      if (!spent) {
        setGuestModal({ open: true, reason: "exhausted" });
        return;
      }
      setLoading(true);
      try {
        const data = await ScanAPI.executeGuest(query);
        setScan(data);
        // Red Scan complete (guest desktop). Hide Red button only —
        // Yellow + textarea remain so the operator can run the
        // optional Fast Fact-Check pass before archive.
        if (FROZEN_VERDICTS.has(data?.verdict?.verdict)) {
          setScanCompleted(true);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const status = err?.response?.status;
        setError(
          typeof detail === "string"
            ? detail
            : "Scan failed. Please retry in a moment."
        );
        if (status === 429) {
          setGuestModal({ open: true, reason: "exhausted" });
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const data = await ScanAPI.execute(query);
      setScan(data);
      // Red Scan complete (main authed path). Hide Red — Yellow stays
      // clickable for the optional second-pass verification. Workspace
      // fully archives only after Yellow Fact-Check completes.
      if (FROZEN_VERDICTS.has(data?.verdict?.verdict)) {
        setScanCompleted(true);
      }
      if (typeof data.credits_remaining === "number") {
        setCredits(data.credits_remaining);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Scan failed. Please retry in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHistory = (s) => {
    setScan(s);
    setError("");
    if (s?.query) setValue(s.query);
    // Rehydrate any saved Fast Fact-Check tied to this scan; otherwise
    // wipe any stray panel left over from the previous workspace state.
    if (s?.fast_check?.sources?.length) {
      setFastResults(s.fast_check);
    } else {
      setFastResults(null);
    }
    // Restoring from history drawer → full archive lock.
    if (FROZEN_VERDICTS.has(s?.verdict?.verdict)) {
      setReadOnly(true);
      setScanCompleted(true);
    }
  };

  return (
    <>
      <OperatorHistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelect={handleSelectHistory}
      />

      <div
        data-testid="scan-interface"
        className="w-full max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-16 pb-14"
      >
        {/* Minimal greeting (replaces the loud hero block) */}
        <div className="mb-8 sm:mb-10 chaos-reveal text-center">
          <h1
            data-testid="hero-greeting"
            className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-[#f1f5f9] leading-tight mb-1.5"
          >
            {isAuthed ? (
              firstName ? (
                <>
                  Hi <span className="text-[#FF0033]">{firstName}</span>, what
                  claim do you want to verify?
                </>
              ) : (
                <>
                  <span className="text-[#FF0033]">Hi.</span> What claim do you
                  want to verify?
                </>
              )
            ) : (
              <>
                What <span className="text-[#FF0033]">claim</span> do you want
                to verify?
              </>
            )}
          </h1>
          <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#94a3b8]">
            Live web evidence · {(() => {
              if (!isAuthed) return "Gemini Light";
              if (user?.plan === "infinite_sub" || user?.is_unlimited) return "Perplexity AI";
              if (user?.plan === "growth_sub") return "Claude 4.5";
              return "Gemini Light";
            })()} verdict · 2 credits per scan
          </p>
        </div>

        {/* Workspace control bar — visible on every viewport */}
        <div
          data-testid="workspace-control-bar"
          className="mb-3 flex flex-wrap gap-2 items-center justify-center"
        >
          <button
            type="button"
            data-testid="new-scan-button"
            onClick={handleNewScan}
            className="group inline-flex items-center justify-center gap-1.5 border border-white/[0.1] hover:border-[#FF0033] bg-[#020617]/60 hover:bg-[#FF0033]/[0.08] text-[#cbd5e1] hover:text-[#FF0033] font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 transition-colors"
          >
            <Plus size={11} />
            [+] New Scan
          </button>
          <button
            type="button"
            data-testid="open-history-button"
            onClick={handleHistoryClick}
            className="group inline-flex items-center justify-center gap-1.5 border border-white/[0.1] hover:border-[#FF0033] bg-[#020617]/60 hover:bg-[#FF0033]/[0.08] text-[#cbd5e1] hover:text-[#FF0033] font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 transition-colors"
          >
            <Archive size={11} />
            [ Operator Logs / History ]
          </button>
          {readOnly && (
            <span
              data-testid="readonly-badge"
              className="inline-flex items-center justify-center gap-1.5 border border-[#FF0033]/50 bg-[#FF0033]/[0.08] text-[#FF0033] font-mono text-[10px] uppercase tracking-[0.24em] px-3 py-1.5"
            >
              <HistoryIcon size={11} />
              Historical record // Read-only
            </span>
          )}
        </div>

        <OperatorGuide />

        <section
          data-testid="hero-terminal"
          className="w-full chaos-reveal"
          style={{ animationDelay: "60ms" }}
        >
          <div
            className={`relative border bg-[#0b0f19]/80 transition-colors duration-200 ${
              focused ? "border-[#FF0033]" : "border-white/[0.08]"
            } ${readOnly ? "opacity-90" : ""}`}
          >
            <span
              aria-hidden
              className={`absolute -top-px -left-px w-3 h-3 border-t border-l ${
                focused ? "border-[#FF0033]" : "border-white/20"
              }`}
            />
            <span
              aria-hidden
              className={`absolute -bottom-px -right-px w-3 h-3 border-b border-r ${
                focused ? "border-[#FF0033]" : "border-white/20"
              }`}
            />
            <textarea
              data-testid="truth-scan-input"
              ref={(el) => {
                if (!el) return;
                // Auto-resize: start short, grow to fit content (capped at ~14 lines).
                el.style.height = "auto";
                const max = 360; // ≈ 14 lines @ leading-relaxed
                const target = Math.min(el.scrollHeight, max);
                el.style.height = `${target}px`;
                el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
              }}
              value={value}
              onChange={(e) => {
                // Smooth auto-grow on every keystroke + soft 480-word cap.
                const el = e.target;
                const raw = el.value;
                const words = raw.trim().split(/\s+/).filter(Boolean);
                if (words.length <= 480) {
                  setValue(raw);
                } else {
                  setValue(words.slice(0, 480).join(" "));
                }
                // Recompute height after value applies.
                el.style.height = "auto";
                const max = 360;
                const target = Math.min(el.scrollHeight, max);
                el.style.height = `${target}px`;
                el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              disabled={readOnly}
              rows={2}
              placeholder="Paste a viral headline, claim, or news string…"
              className="w-full min-h-[56px] bg-transparent text-[#f1f5f9] p-4 sm:p-5 text-[16px] sm:text-[17px] leading-relaxed resize-none focus:outline-none placeholder:text-[#64748b] disabled:cursor-not-allowed transition-[height] duration-150 ease-out"
              style={{ fontFamily: "Geist, ui-sans-serif, system-ui, sans-serif" }}
            />
            <div className="flex items-center justify-between border-t border-white/[0.06] px-4 sm:px-5 py-1.5 font-mono text-[9px] uppercase tracking-widest text-[#94a3b8]">
              <span className="flex items-center gap-2">
                <span
                  className={`inline-block w-1.5 h-1.5 ${
                    readOnly
                      ? "bg-[#FF0033]"
                      : value.length > 0
                      ? "bg-[#FF0033] chaos-pulse"
                      : "bg-[#64748b]"
                  }`}
                  aria-hidden
                />
                {readOnly
                  ? "LOCKED"
                  : value.length > 0
                  ? "BUFFER LOADED"
                  : "AWAITING INPUT"}
              </span>
              <span data-testid="char-counter" className="tabular-nums">
                {value.trim().split(/\s+/).filter(Boolean).length} / 480 words
              </span>
            </div>
          </div>

          {!readOnly && (
            <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {!scanCompleted && (
                <button
                  data-testid="execute-scan-button"
                  type="button"
                  onClick={handleExecute}
                  disabled={loading}
                  className="group inline-flex items-center justify-center gap-2 bg-[#FF0033] hover:bg-white hover:text-black disabled:bg-[#1e293b] disabled:text-[#94a3b8] disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.18em] px-5 sm:px-7 py-2.5 sm:py-3 text-[11px] sm:text-xs border border-transparent hover:border-white transition-colors duration-200 cursor-pointer w-full sm:w-auto"
                >
                  {loading ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      SCANNING…
                    </>
                  ) : isAuthed ? (
                    <>
                      <ShieldAlert size={13} />
                      Execute scan · 2 credits
                      <ArrowRight size={13} />
                    </>
                  ) : guestExhausted ? (
                    <>
                      <Lock size={13} />
                      Sign in for 7 free credits
                      <ArrowRight size={13} />
                    </>
                  ) : (
                    <>
                      <ShieldAlert size={13} />
                      Execute scan · 2 credits
                      <ArrowRight size={13} />
                    </>
                  )}
                </button>
              )}

              {scanCompleted && (
                <p
                  data-testid="scan-completed-helper"
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300/90 leading-snug max-w-md"
                >
                  ✓ Scan complete. Run a [Fast Fact-Check] to finalize this
                  verdict, or tap [+] New Scan to start fresh.
                </p>
              )}

              <button
                data-testid="fast-fact-check-button"
                type="button"
                onClick={handleFastCheck}
                disabled={fastChecking || loading}
                title="Run a quick source-only background check — no credit deducted."
                className="group inline-flex items-center justify-center gap-2 border border-white/[0.12] hover:border-amber-400 bg-transparent hover:bg-amber-400/[0.08] text-[#cbd5e1] hover:text-amber-300 disabled:opacity-60 disabled:cursor-not-allowed font-mono uppercase tracking-[0.18em] px-4 sm:px-5 py-2.5 sm:py-3 text-[10.5px] sm:text-[11px] transition-colors w-full sm:w-auto"
              >
                {fastChecking ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Checking sources…
                  </>
                ) : (
                  <>
                    <Zap size={12} className="text-amber-400" />
                    Fast Fact-Check (0 Credits)
                  </>
                )}
              </button>
            </div>
          )}

          {readOnly && (
            <p
              data-testid="readonly-helper"
              className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#94a3b8] flex items-center gap-2"
            >
              <Lock size={11} className="text-[#FF0033]" />
              This historical record is frozen. Tap [+] New Scan to start a
              fresh validation routine.
            </p>
          )}

          {error && (
            <div
              data-testid="scan-error"
              className="mt-3 border border-[#FF0033]/40 bg-[#FF0033]/[0.08] px-3.5 py-2.5 flex items-start gap-2"
            >
              <AlertTriangle
                size={13}
                className="text-[#FF0033] mt-0.5 shrink-0"
              />
              <p className="font-mono text-[11px] text-[#f1f5f9] leading-relaxed break-words flex-1 min-w-0">
                {error}
              </p>
              <button
                type="button"
                data-testid="scan-error-dismiss"
                onClick={() => setError("")}
                aria-label="Dismiss alert"
                className="shrink-0 w-6 h-6 flex items-center justify-center text-[#cbd5e1] hover:text-[#FF0033] transition-colors -mt-0.5"
              >
                <X size={13} />
              </button>
            </div>
          )}

          {/* Terminal disclaimer — visible under every chat surface */}
          <p
            data-testid="terminal-ai-disclaimer"
            className="mt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-[#cbd5e1] text-center"
          >
            AI can make mistakes. Please double-check your sources.
          </p>

          {fastChecking === false && null}
          <FastCheckPanel variant="inline" />
        </section>

        {/* Evidence section only renders AFTER a scan runs — minimal cold state. */}
        {hasResults && (
          <section
            data-testid="evidence-networks"
            className="w-full chaos-reveal mt-10 lg:mt-12"
            style={{ animationDelay: "180ms" }}
          >
            <div className="flex items-end justify-between gap-3 mb-4 lg:mb-5 border-t border-white/[0.06] pt-6 lg:pt-8">
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="inline-block w-2 h-2 bg-[#FF0033] chaos-pulse"
                />
                <h2
                  data-testid="evidence-section-title"
                  className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]"
                >
                  Live Evidence Network
                </h2>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-widest text-[#94a3b8] tabular-nums">
                {`${String(scan.evidence.length).padStart(2, "0")} NODES`}
              </span>
            </div>

            {scan?.query && (
              <div
                data-testid="scan-query-summary"
                className="mb-4 lg:mb-5 border border-white/[0.06] bg-white/[0.02] px-3.5 py-2.5 font-mono text-[11px] text-[#cbd5e1]"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF0033] mb-1">
                  Verified claim
                </div>
                <div className="break-words" title={scan.query}>
                  {scan.query}
                </div>
              </div>
            )}

            {scan?.verdict && <VerdictPanel verdict={scan.verdict} />}

            <ul
              data-testid="evidence-list"
              className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4"
            >
              {scan.evidence.map((item, idx) => (
                <EvidenceCard key={item.id || idx} item={item} idx={idx} />
              ))}
            </ul>

            <div className="mt-5 lg:mt-6 flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-[#94a3b8]">
              <span className="flex items-center gap-2">
                <Activity size={10} className="text-[#FF0033]" />
                STATUS: {loading ? "SCANNING" : readOnly ? "ARCHIVED" : "RESULTS LIVE"}
              </span>
              <span>BUILD 0.6.0</span>
            </div>
          </section>
        )}
      </div>

      <GuestExhaustedModal
        open={guestModal.open}
        reason={guestModal.reason}
        onClose={() => setGuestModal({ open: false, reason: null })}
        onAuth={() => {
          setGuestModal({ open: false, reason: null });
          openAuthModal();
        }}
      />
    </>
  );
};

export default ScanInterface;
